import {
    Banknote,
    CreditCard,
    Gift,
    Sparkles,
} from "lucide-react";
import { ReactNode } from "react";

export type ProductDetailProduct = {
    id: number | string;
    title: string;
    description: string;
    image: string;
    category?: string;
    badge?: string;
    price: number;
    mrp: number;
    brand?: string;
    rating?: number;
    reviews?: string;
    colors?: {
        name: string;
        value?: string;
        hex?: string;
        image?: string;
    }[];
    variants?: {
        label: string;
        price: number;
        mrp: number;
    }[];
    quantityOptions?: {
        label: string;
        price: number;
        mrp?: number;
    }[];
    bulletPoints?: string[];
    attributes?: Record<string, any>;
    sizes?: string[];
    country?: string;
    packaging?: string;
    vendors?: {
        manufacturedBy?: string;
        marketedBy?: string;
    };
    freeDelivery?: boolean;
    fastDelivery?: boolean;
    nextDayDelivery?: boolean;
    shipsFrom?: string;
    payment?: string;
    warranty?: string;
    returnPolicy?: string;
    faqs?: {
        question: string;
        answer: string;
    }[];
};

export type ProductDetailFaq = {
    question: string;
    answer: string;
};

export type ProductDetailTabRow = {
    label: string;
    value: string;
};

export type ProductDetailTabGroup = {
    title: string;
    rows: ProductDetailTabRow[];
};

export type ProductDetailTab = {
    id: string;
    label: string;
    description?: string;
    groups: ProductDetailTabGroup[];
};

export type DetailSection = {
    title: string;
    subtitle: string;
    rows?: {
        label: string;
        value: string;
    }[];
    bullets?: string[];
    paragraph?: string;
    faqs?: ProductDetailFaq[];
    tabs?: ProductDetailTab[];
};

type DetailContent = {
    storeName: string;
    pickLabel: string;
    pickText: string;
    colorLabel?: string;
    sizeLabel?: string;
    showSizeGuide?: boolean;
    colors?: {
        name: string;
        value?: string;
        hex?: string;
        image?: string;
    }[];
    sizes?: string[];
    offers: {
        title: string;
        text: string;
        action: string;
        icon: ReactNode;
    }[];
    policies: {
        shippingTitle?: string;
        shipping: string;
        deliveryTitle?: string;
        delivery: string;
        returnsTitle?: string;
        returns: string;
        paymentTitle?: string;
        payment: string;
        securityTitle?: string;
        security?: string;
    };
    reviewCount: string;
    detailSections: DetailSection[];
};

const offers = [
    {
        title: "Salar Shopping Voucher",
        text: "Use eligible Salar vouchers and promotional rewards to unlock extra savings at checkout.",
        action: "View vouchers",
        icon: <Gift size={20} />,
    },
    {
        title: "Bank Offer",
        text: "Get instant savings on eligible credit cards, debit cards and EMI transactions.",
        action: "View bank offers",
        icon: <CreditCard size={20} />,
    },

    {
        title: "No Cost EMI",
        text: "Convert your purchase into convenient monthly instalments on eligible bank cards.",
        action: "Check EMI plans",
        icon: <Banknote size={20} />,
    },
    {
        title: "Member Benefit",
        text: "Eligible Salar members can unlock additional rewards and selected product benefits.",
        action: "See member benefits",
        icon: <Sparkles size={20} />,
    },
];

const fashionDetails = (
    product: ProductDetailProduct,
    women: boolean
): DetailContent => ({
    storeName: women
        ? "Salar Fashion Studio"
        : "Salar Menswear",

    pickLabel:
        product.badge || "SALAR CHOICE",

    pickText:
        "Popular for fit, quality and overall value",

    colorLabel: "Colours",
    sizeLabel: "Select Size",
    showSizeGuide: true,

    colors: product.colors?.length
        ? product.colors.map((color) => ({
            ...color,
            image: color.image || product.image,
        }))
        : women
            ? [
                {
                    name: "Rose",
                    value: "#c95f7e",
                    image: product.image,
                },
                {
                    name: "Black",
                    value: "#222222",
                    image: product.image,
                },
                {
                    name: "Ivory",
                    value: "#ddd3c3",
                    image: product.image,
                },
                {
                    name: "Wine",
                    value: "#8e3950",
                    image: product.image,
                },
            ]
            : [
                {
                    name: "Navy",
                    value: "#26384f",
                    image: product.image,
                },
                {
                    name: "Black",
                    value: "#222222",
                    image: product.image,
                },
                {
                    name: "White",
                    value: "#e9e9e9",
                    image: product.image,
                },
                {
                    name: "Olive",
                    value: "#66724c",
                    image: product.image,
                },
            ],

    sizes: [
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "2XL",
    ],

    offers,

    policies: {
        shipping:
            "Priority dispatch available",
        delivery:
            "Free on eligible orders",
        returns:
            "7-day return & exchange",
        payment:
            "Available on eligible pincodes",
    },

    reviewCount: women
        ? "8.4K"
        : "7.9K",

    detailSections: [
        {
            title: "Top Highlights",
            subtitle:
                "Core product specifications",

            rows: women
                ? [
                    {
                        label: "Material Composition",
                        value: "Premium rayon blend",
                    },
                    {
                        label: "Fit Type",
                        value: "Comfort fit",
                    },
                    {
                        label: "Pattern",
                        value: "Contemporary fashion",
                    },
                    {
                        label: "Neck Style",
                        value: "Modern neckline",
                    },
                    {
                        label: "Sleeve Type",
                        value: "Regular sleeves",
                    },
                    {
                        label: "Length",
                        value: "Standard fashion length",
                    },
                    {
                        label: "Occasion",
                        value: "Casual, work & occasion wear",
                    },
                    {
                        label: "Care Instructions",
                        value: "Gentle machine wash",
                    },
                ]
                : [
                    {
                        label: "Material Composition",
                        value: "Premium cotton blend",
                    },
                    {
                        label: "Fit Type",
                        value: "Regular fit",
                    },
                    {
                        label: "Pattern",
                        value: "Modern casual",
                    },
                    {
                        label: "Collar Style",
                        value: "Classic collar",
                    },
                    {
                        label: "Sleeve Type",
                        value: "Full sleeves",
                    },
                    {
                        label: "Closure Type",
                        value: "Button closure",
                    },
                    {
                        label: "Occasion",
                        value: "Casual, work & smart casual",
                    },
                    {
                        label: "Care Instructions",
                        value: "Machine wash",
                    },
                ],
        },
        {
            title: "About this item",
            subtitle:
                "Why this product stands out",

            bullets: [
                "Designed for comfortable all-day wear with a clean and premium finish.",
                "Fabric is selected for softness, breathability and practical everyday use.",
                "Versatile styling works well for casual outings, work and relaxed occasions.",
                "Thoughtful construction helps the garment maintain its shape and appearance.",
                "Available in multiple colours and sizes to make choosing the right fit easier.",
                "Easy-care construction makes it suitable for regular wardrobe rotation.",
            ],
        },
        {
            title: "Fit & Style Details",
            subtitle:
                "Sizing and styling information",

            rows: [
                {
                    label: "Fit Recommendation",
                    value: "Choose your usual size for a regular fit",
                },
                {
                    label: "Styling",
                    value: women
                        ? "Pairs well with denim, trousers and layered looks"
                        : "Pairs well with denim, chinos and casual trousers",
                },
                {
                    label: "Season",
                    value: "All-season everyday wear",
                },
                {
                    label: "Stretch",
                    value: "Comfort movement",
                },
                {
                    label: "Transparency",
                    value: "Designed for regular everyday coverage",
                },
            ],
        },
        {
            title: "Additional Information",
            subtitle:
                "Product and manufacturing information",

            rows: [
                {
                    label: "Country of Origin",
                    value: "India",
                },
                {
                    label: "Department",
                    value: women
                        ? "Women"
                        : "Men",
                },
                {
                    label: "Category",
                    value: "Fashion & Accessories",
                },
                {
                    label: "Net Quantity",
                    value: "1 unit",
                },
                {
                    label: "Pack Type",
                    value: "Retail pack",
                },
                {
                    label: "Seller Support",
                    value: "Salar seller support",
                },
            ],
        },
        {
            title: "Item Details",
            subtitle:
                "More useful information",

            paragraph:
                `${product.title} is positioned as a versatile fashion essential with an emphasis on comfortable construction, clean finishing, wearable styling and practical everyday maintenance.`,
        },
    ],
});

const electronicsDetails = (
    product: ProductDetailProduct
): DetailContent => ({
    storeName: "Salar Electronics",

    pickLabel:
        product.badge || "TECH PICK",

    pickText:
        "Selected for performance, features and customer demand",

    colorLabel: "Colour",
    sizeLabel: product.variants?.length
        ? "Select Configuration"
        : undefined,

    colors: [
        {
            name: "Graphite",
            value: "#454545",
            image: product.image,
        },
        {
            name: "Silver",
            value: "#c8ccd2",
            image: product.image,
        },
        {
            name: "Blue",
            value: "#6f89a8",
            image: product.image,
        },
    ],

    sizes:
        product.variants?.map(
            (item) => item.label
        ),

    offers,

    policies: {
        shipping:
            "Priority electronics dispatch",
        delivery:
            "Free on eligible orders",
        returns:
            "7-day replacement policy",
        payment:
            "Secure EMI & prepaid options",
    },

    reviewCount: "12.6K",

    detailSections: [
        {
            title: "Top Highlights",
            subtitle:
                "Key hardware and experience",

            rows: [
                {
                    label: "Product Type",
                    value: product.title.includes(
                        "AirPods"
                    )
                        ? "True Wireless Audio"
                        : "Smart Device",
                },
                {
                    label: "Performance",
                    value: "Flagship-class everyday performance",
                },
                {
                    label: "Display / Audio",
                    value: "High-quality multimedia experience",
                },
                {
                    label: "Camera / Sound",
                    value: "Advanced content and communication features",
                },
                {
                    label: "Connectivity",
                    value: "Wi-Fi, Bluetooth and modern wireless support",
                },
                {
                    label: "Storage / Variant",
                    value:
                        product.variants?.[0]
                            ?.label ||
                        "Standard configuration",
                },
                {
                    label: "Security",
                    value: "Modern device security features",
                },
                {
                    label: "Warranty",
                    value: "Manufacturer warranty",
                },
            ],
        },
        {
            title: "About this item",
            subtitle:
                "Key reasons to consider",

            bullets: [
                "Designed for smooth daily performance, entertainment and productivity.",
                "Premium hardware with modern connectivity and ecosystem integration.",
                "Optimised for secure everyday use with reliable software support.",
                "Supports fast wireless connectivity for compatible accessories and networks.",
                "Eligible variants allow you to choose the configuration that suits your needs.",
                "Built for a balanced mix of performance, usability and long-term value.",
            ],
        },
        {
            title: "Technical Specifications",
            subtitle:
                "Device specifications",

            rows: [
                {
                    label: "Wireless",
                    value: "Bluetooth / Wi-Fi where supported",
                },
                {
                    label: "Power",
                    value: "Rechargeable battery / mains where applicable",
                },
                {
                    label: "Interface",
                    value: "Modern digital interface",
                },
                {
                    label: "Included Components",
                    value: "Device, documentation and standard accessories",
                },
                {
                    label: "Compatibility",
                    value: "Compatible ecosystem devices and services",
                },
                {
                    label: "Software",
                    value: "Manufacturer-supported software experience",
                },
            ],
        },
        {
            title: "Warranty & Support",
            subtitle:
                "After-sales information",

            rows: [
                {
                    label: "Warranty",
                    value: "Manufacturer warranty applies",
                },
                {
                    label: "Service Support",
                    value: "Authorised service network",
                },
                {
                    label: "Replacement",
                    value: "As per category replacement policy",
                },
                {
                    label: "Seller Support",
                    value: "Salar seller assistance",
                },
            ],
        },
        {
            title: "Additional Information",
            subtitle:
                "Origin and product information",

            rows: [
                {
                    label: "Country of Origin",
                    value: "As per product packaging",
                },
                {
                    label: "Category",
                    value: "Electronics",
                },
                {
                    label: "Net Quantity",
                    value: "1 unit",
                },
                {
                    label: "Package Type",
                    value: "Retail package",
                },
            ],
        },
    ],
});

const applianceDetails = (
    product: ProductDetailProduct
): DetailContent => ({
    storeName:
        "Salar Home & Kitchen",

    pickLabel:
        product.badge || "SMART HOME PICK",

    pickText:
        "Chosen for utility, reliability and value",

    colorLabel: "Finish",
    sizeLabel: "Select Variant",

    colors: [
        {
            name: "White",
            value: "#ededed",
            image: product.image,
        },
        {
            name: "Black",
            value: "#282828",
            image: product.image,
        },
        {
            name: "Steel",
            value: "#aeb6bd",
            image: product.image,
        },
    ],

    sizes:
        product.variants?.map(
            (item) => item.label
        ) ?? ["Standard"],

    offers,

    policies: {
        shipping:
            "Safe appliance shipping",
        delivery:
            "Free on eligible orders",
        returns:
            "Replacement as per appliance policy",
        payment:
            "EMI available on eligible products",
    },

    reviewCount: "6.8K",

    detailSections: [
        {
            title: "Top Highlights",
            subtitle:
                "Power, capacity and convenience",

            rows: [
                {
                    label: "Product Type",
                    value: product.title,
                },
                {
                    label: "Power / Capacity",
                    value:
                        product.variants?.[0]
                            ?.label ||
                        "Efficient everyday performance",
                },
                {
                    label: "Usage",
                    value: "Everyday household use",
                },
                {
                    label: "Controls",
                    value: "Easy-access controls",
                },
                {
                    label: "Safety Features",
                    value: "Built-in safety protection",
                },
                {
                    label: "Body / Finish",
                    value: "Durable easy-care construction",
                },
                {
                    label: "Cleaning",
                    value: "Easy-maintenance design",
                },
                {
                    label: "Warranty",
                    value: "Manufacturer warranty",
                },
            ],
        },
        {
            title: "About this item",
            subtitle:
                "Everyday benefits",

            bullets: [
                "Designed to simplify routine kitchen and home tasks.",
                "Efficient performance with practical controls for everyday use.",
                "Durable construction is suitable for regular household operation.",
                "Easy-clean design reduces routine maintenance effort.",
                "Multiple variants are available where applicable.",
                "Safety-focused features support convenient day-to-day operation.",
            ],
        },
        {
            title: "Installation & Usage",
            subtitle:
                "Setup and operating information",

            rows: [
                {
                    label: "Installation",
                    value: "Plug-and-use unless otherwise specified",
                },
                {
                    label: "Power Source",
                    value: "Electric",
                },
                {
                    label: "Recommended Use",
                    value: "Indoor household use",
                },
                {
                    label: "Operating Guidance",
                    value: "Use according to manufacturer instructions",
                },
                {
                    label: "Cleaning Guidance",
                    value: "Allow unit to cool and follow product manual",
                },
            ],
        },
        {
            title: "In the Box",
            subtitle:
                "Package contents",

            rows: [
                {
                    label: "Main Unit",
                    value: "1",
                },
                {
                    label: "Accessories",
                    value: "Standard accessories as applicable",
                },
                {
                    label: "Documentation",
                    value: "User manual / warranty information",
                },
            ],
        },
        {
            title: "Additional Information",
            subtitle:
                "Warranty and support",

            rows: [
                {
                    label: "Warranty",
                    value: "Manufacturer warranty",
                },
                {
                    label: "Category",
                    value: "Home & Kitchen Appliances",
                },
                {
                    label: "Country of Origin",
                    value: "As per product label",
                },
                {
                    label: "Seller Support",
                    value: "Salar seller assistance",
                },
            ],
        },
    ],
});

const groceryDetails = (
    product: ProductDetailProduct
): DetailContent => ({
    storeName:
        "Salar Fresh & Grocery",

    pickLabel:
        product.badge || "PANTRY PICK",

    pickText:
        "Picked for quality, freshness and everyday value",

    sizeLabel: "Select Pack Size",

    sizes:
        product.quantityOptions?.map(
            (item) => item.label
        ) ?? ["Standard Pack"],

    offers: offers.slice(0, 3),

    policies: {
        shipping:
            "Fresh-safe packing",
        delivery:
            "Fast delivery where available",
        returns:
            "Food-category policy applies",
        payment:
            "Prepaid & eligible COD",
    },

    reviewCount: "4.2K",

    detailSections: [
        {
            title: "Top Highlights",
            subtitle:
                "Pack, quality and storage",

            rows: [
                {
                    label: "Product",
                    value: product.title,
                },
                {
                    label: "Pack Size",
                    value:
                        product.quantityOptions?.[0]
                            ?.label ||
                        "Standard pack",
                },
                {
                    label: "Category",
                    value: "Grocery & Everyday Needs",
                },
                {
                    label: "Storage",
                    value: "Store in a cool, dry place",
                },
                {
                    label: "Usage",
                    value: "Everyday household use",
                },
                {
                    label: "Packaging",
                    value: "Sealed retail pack",
                },
                {
                    label: "Shelf Life",
                    value: "Refer to physical package",
                },
                {
                    label: "Country of Origin",
                    value: "As declared on pack",
                },
            ],
        },
        {
            title: "About this item",
            subtitle:
                "Quality and usage",

            bullets: [
                "Selected for everyday quality and reliable household use.",
                "Packed to help preserve freshness, aroma and product integrity.",
                "Suitable for regular consumption according to pack instructions.",
                "Choose from available pack sizes based on your household requirement.",
                "Always check the physical pack for final date, allergen and storage information.",
            ],
        },
        {
            title: "Ingredients & Nutrition",
            subtitle:
                "Food information",

            paragraph:
                "Ingredients, allergen declarations and nutritional values differ by product and pack. Always refer to the physical product label for the final manufacturer-declared information.",
        },
        {
            title: "Storage & Safety",
            subtitle:
                "Handling recommendations",

            rows: [
                {
                    label: "Storage",
                    value: "Cool and dry place",
                },
                {
                    label: "After Opening",
                    value: "Keep sealed as recommended on pack",
                },
                {
                    label: "Food Safety",
                    value: "Follow manufacturer label instructions",
                },
                {
                    label: "Allergens",
                    value: "Refer to product packaging",
                },
            ],
        },
        {
            title: "Additional Information",
            subtitle:
                "Origin and seller information",

            rows: [
                {
                    label: "Country of Origin",
                    value: "As declared on pack",
                },
                {
                    label: "Category",
                    value: "Grocery",
                },
                {
                    label: "Net Quantity",
                    value:
                        product.quantityOptions?.[0]
                            ?.label ||
                        "As per pack",
                },
                {
                    label: "Seller Support",
                    value: "Salar grocery support",
                },
            ],
        },
    ],
});

const buildFashionFaqs = (
    product: ProductDetailProduct,
    women: boolean
): ProductDetailFaq[] => [
        {
            question:
                "What material is this product made from?",
            answer:
                women
                    ? `${product.title} is described in this product page as using a premium rayon-blend construction selected for comfort, drape and everyday wear.`
                    : `${product.title} is described in this product page as using a premium cotton-blend construction selected for softness, breathability and regular everyday wear.`,
        },
        {
            question:
                "How do I choose the right size?",
            answer:
                "Use the size selector and Size Guide shown above before placing your order. For the regular fit described on this page, choosing your usual size is the recommended starting point.",
        },
        {
            question:
                "Is this suitable for everyday use?",
            answer:
                "Yes. The product is positioned for comfortable everyday wear and can be styled for casual outings, work and other relaxed occasions.",
        },
        {
            question:
                "How should I wash and care for it?",
            answer:
                women
                    ? "Gentle machine washing is recommended. Follow the care label on the physical product for the final wash temperature, drying and ironing instructions."
                    : "Machine washing is supported for this item. Follow the care label on the physical product for the final wash temperature, drying and ironing instructions.",
        },
        {
            question:
                "Can I return or exchange this item?",
            answer:
                "Eligible orders can use the 7-day return and exchange policy shown in the Shopping policies section. Final eligibility can depend on the item condition, category rules and order status.",
        },
        {
            question:
                "Will the colour look exactly the same as the image?",
            answer:
                "The selected colour is represented as closely as possible, but small differences can occur because of screen settings, lighting and photography. Check the selected colour name before checkout.",
        },
    ];

const buildElectronicsFaqs = (
    product: ProductDetailProduct
): ProductDetailFaq[] => [
        {
            question:
                "What is included with this product?",
            answer:
                `${product.title} includes the device, standard documentation and applicable standard accessories. The exact final box contents should be confirmed from the product packaging and manufacturer information.`,
        },
        {
            question:
                "Does this product include a warranty?",
            answer:
                "Manufacturer warranty applies as described in the Warranty & Support section. Warranty duration and coverage are governed by the manufacturer and the specific product variant.",
        },
        {
            question:
                "How do I check compatibility before buying?",
            answer:
                "Review the selected configuration, connectivity and compatibility details on this page. For accessories or ecosystem features, confirm that your existing device, operating system and required standards are supported.",
        },
        {
            question:
                "Can this product be replaced if there is an issue?",
            answer:
                "Eligible electronics orders follow the replacement policy displayed in Shopping policies. The final replacement decision depends on category rules, order status and verification of the reported issue.",
        },
        {
            question:
                "Which colour or configuration will I receive?",
            answer:
                "You will receive the colour and configuration selected on the product page before checkout. Recheck the active colour and configuration buttons before placing the order.",
        },
        {
            question:
                "Are EMI and secure online payment options available?",
            answer:
                "Eligible products can support EMI and secure prepaid payment options. Availability can vary by bank, payment provider, order value and checkout eligibility.",
        },
    ];

const buildApplianceFaqs = (
    product: ProductDetailProduct
): ProductDetailFaq[] => [
        {
            question:
                "Does this appliance require installation?",
            answer:
                `${product.title} is described as plug-and-use unless otherwise specified. If the selected model requires installation, follow the manufacturer instructions or authorised service guidance.`,
        },
        {
            question:
                "Is a manufacturer warranty included?",
            answer:
                "Manufacturer warranty applies where stated for the selected product. Refer to the warranty information supplied with the appliance for the exact period, parts and service coverage.",
        },
        {
            question:
                "What power source does this product use?",
            answer:
                "This appliance is intended for electric household use. Confirm the voltage, wattage and socket requirements from the physical product label or manufacturer manual before operation.",
        },
        {
            question:
                "How should I clean and maintain the appliance?",
            answer:
                "Allow the unit to cool where applicable, disconnect power before cleaning when required, and follow the manufacturer cleaning instructions. Avoid methods that are not approved for the specific appliance.",
        },
        {
            question:
                "Can the appliance be returned or replaced?",
            answer:
                "Replacement is available according to the applicable appliance policy shown on the product page. Final eligibility depends on category conditions, item status and verification.",
        },
        {
            question:
                "What comes inside the package?",
            answer:
                "The package includes the main unit, applicable standard accessories and product documentation. Exact accessories can vary by model or selected variant.",
        },
    ];

const buildGroceryFaqs = (
    product: ProductDetailProduct
): ProductDetailFaq[] => [
        {
            question:
                "Where can I check the expiry or best-before date?",
            answer:
                `${product.title} uses the date information printed on the physical pack as the final reference. Always check the delivered package for manufacturing, expiry or best-before details before use.`,
        },
        {
            question:
                "How should this product be stored?",
            answer:
                "Store it in a cool, dry place unless the physical package gives different instructions. After opening, keep the pack sealed or store it exactly as recommended by the manufacturer.",
        },
        {
            question:
                "Where can I find ingredient and allergen information?",
            answer:
                "Ingredients, allergen declarations and nutritional values can vary by product and pack. The physical manufacturer label is the final source for this information.",
        },
        {
            question:
                "Which pack size will I receive?",
            answer:
                "You will receive the pack size selected on this product page before checkout. Recheck the selected pack or quantity option before placing the order.",
        },
        {
            question:
                "Can grocery products be returned?",
            answer:
                "Food and grocery items follow category-specific policies. Return or replacement eligibility depends on the product type, delivered condition and the reason reported.",
        },
        {
            question:
                "Where is this product made?",
            answer:
                "Country-of-origin information should be checked on the physical pack because the manufacturer-declared packaging information is the final reference.",
        },
    ];


const joinColorNames = (
    product: ProductDetailProduct,
    fallback: string
) =>
    product.colors?.length
        ? product.colors
            .map(
                (
                    color
                ) =>
                    color.name
            )
            .join(", ")
        : fallback;

const buildFashionItemDetails = (
    product: ProductDetailProduct,
    women: boolean
): ProductDetailTab[] => [
        {
            id: "specifications",
            label: "Specifications",
            description:
                "Complete product specifications, fit and care information.",
            groups: [
                {
                    title: "General",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Product Name",
                            value:
                                product.title,
                        },
                        {
                            label: "Category",
                            value:
                                "Fashion & Accessories",
                        },
                        {
                            label: "Department",
                            value:
                                women
                                    ? "Women"
                                    : "Men",
                        },
                        {
                            label: "Available Colours",
                            value:
                                joinColorNames(
                                    product,
                                    women
                                        ? "Rose, Black, Ivory, Wine"
                                        : "Navy, Black, White, Olive"
                                ),
                        },
                        {
                            label: "Net Quantity",
                            value:
                                "1 unit",
                        },
                    ],
                },
                {
                    title: "Material & Fit",
                    rows: [
                        {
                            label: "Material Composition",
                            value:
                                women
                                    ? "Premium rayon blend"
                                    : "Premium cotton blend",
                        },
                        {
                            label: "Fit Type",
                            value:
                                women
                                    ? "Comfort fit"
                                    : "Regular fit",
                        },
                        {
                            label: "Pattern",
                            value:
                                women
                                    ? "Contemporary fashion"
                                    : "Modern casual",
                        },
                        {
                            label: "Sleeve Type",
                            value:
                                "Regular sleeves",
                        },
                        {
                            label: "Occasion",
                            value:
                                women
                                    ? "Casual, work & occasion wear"
                                    : "Casual, work & smart casual",
                        },
                        {
                            label: "Care Instructions",
                            value:
                                women
                                    ? "Gentle machine wash"
                                    : "Machine wash",
                        },
                    ],
                },
            ],
        },
        {
            id: "warranty",
            label: "Warranty",
            description:
                "Warranty, returns and after-sales support information.",
            groups: [
                {
                    title: "Warranty & Support",
                    rows: [
                        {
                            label: "Warranty Summary",
                            value:
                                "No manufacturer warranty unless specifically stated for the selected product.",
                        },
                        {
                            label: "Returns & Exchange",
                            value:
                                "7-day return & exchange on eligible orders.",
                        },
                        {
                            label: "Seller Support",
                            value:
                                "Salar seller support",
                        },
                        {
                            label: "Policy Note",
                            value:
                                "Final eligibility depends on product condition, category rules and order status.",
                        },
                    ],
                },
            ],
        },
        {
            id: "manufacturer",
            label: "Manufacturer Info",
            description:
                "Origin, manufacturer and packaging information.",
            groups: [
                {
                    title: "Manufacturer Details",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Manufacturer",
                            value:
                                "As declared on the physical product label",
                        },
                        {
                            label: "Packer",
                            value:
                                "As declared on the retail packaging",
                        },
                        {
                            label: "Country of Origin",
                            value:
                                "India",
                        },
                        {
                            label: "Generic Name",
                            value:
                                women
                                    ? "Women's Fashion"
                                    : "Men's Fashion",
                        },
                        {
                            label: "Pack Type",
                            value:
                                "Retail pack",
                        },
                    ],
                },
            ],
        },
    ];

const buildElectronicsItemDetails = (
    product: ProductDetailProduct
): ProductDetailTab[] => [
        {
            id: "specifications",
            label: "Specifications",
            description:
                "Technical, connectivity and package specifications.",
            groups: [
                {
                    title: "General",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Product Name",
                            value:
                                product.title,
                        },
                        {
                            label: "Product Type",
                            value:
                                product.title.includes(
                                    "AirPods"
                                )
                                    ? "True Wireless Audio"
                                    : product.title
                                        .toLowerCase()
                                        .includes(
                                            "iphone"
                                        )
                                        ? "Smartphone"
                                        : "Smart Device",
                        },
                        {
                            label: "Configuration",
                            value:
                                product.variants?.[0]
                                    ?.label ||
                                "Standard configuration",
                        },
                        {
                            label: "Available Colours",
                            value:
                                joinColorNames(
                                    product,
                                    "Graphite, Silver, Blue"
                                ),
                        },
                        {
                            label: "Net Quantity",
                            value:
                                "1 unit",
                        },
                    ],
                },
                {
                    title: "Connectivity & Power",
                    rows: [
                        {
                            label: "Wireless",
                            value:
                                "Bluetooth / Wi-Fi where supported",
                        },
                        {
                            label: "Power",
                            value:
                                "Rechargeable battery / mains where applicable",
                        },
                        {
                            label: "Interface",
                            value:
                                "Modern digital interface",
                        },
                        {
                            label: "Compatibility",
                            value:
                                "Compatible ecosystem devices and services",
                        },
                        {
                            label: "Software",
                            value:
                                "Manufacturer-supported software experience",
                        },
                        {
                            label: "Included Components",
                            value:
                                "Device, documentation and standard accessories",
                        },
                    ],
                },
            ],
        },
        {
            id: "warranty",
            label: "Warranty",
            description:
                "Manufacturer warranty and replacement information.",
            groups: [
                {
                    title: "Warranty Details",
                    rows: [
                        {
                            label: "Warranty Summary",
                            value:
                                "Manufacturer warranty applies",
                        },
                        {
                            label: "Service Support",
                            value:
                                "Authorised service network",
                        },
                        {
                            label: "Replacement",
                            value:
                                "7-day replacement policy on eligible electronics orders",
                        },
                        {
                            label: "Seller Support",
                            value:
                                "Salar seller assistance",
                        },
                    ],
                },
            ],
        },
        {
            id: "manufacturer",
            label: "Manufacturer Info",
            description:
                "Manufacturer, origin and packaging information.",
            groups: [
                {
                    title: "Manufacturer Details",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Manufacturer",
                            value:
                                "As declared on the product packaging",
                        },
                        {
                            label: "Packer",
                            value:
                                "As declared on the retail package",
                        },
                        {
                            label: "Country of Origin",
                            value:
                                "As per product packaging",
                        },
                        {
                            label: "Generic Name",
                            value:
                                "Electronics",
                        },
                        {
                            label: "Package Type",
                            value:
                                "Retail package",
                        },
                    ],
                },
            ],
        },
    ];

const buildApplianceItemDetails = (
    product: ProductDetailProduct
): ProductDetailTab[] => [
        {
            id: "specifications",
            label: "Specifications",
            description:
                "Product, usage, power and package specifications.",
            groups: [
                {
                    title: "General",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Product Name",
                            value:
                                product.title,
                        },
                        {
                            label: "Category",
                            value:
                                "Home & Kitchen Appliances",
                        },
                        {
                            label: "Variant",
                            value:
                                product.variants?.[0]
                                    ?.label ||
                                "Standard",
                        },
                        {
                            label: "Power Source",
                            value:
                                "Electric",
                        },
                        {
                            label: "Recommended Use",
                            value:
                                "Indoor household use",
                        },
                    ],
                },
                {
                    title: "Usage & Package",
                    rows: [
                        {
                            label: "Installation",
                            value:
                                "Plug-and-use unless otherwise specified",
                        },
                        {
                            label: "Controls",
                            value:
                                "Easy-access controls",
                        },
                        {
                            label: "Safety Features",
                            value:
                                "Built-in safety protection",
                        },
                        {
                            label: "Main Unit",
                            value:
                                "1",
                        },
                        {
                            label: "Accessories",
                            value:
                                "Standard accessories as applicable",
                        },
                        {
                            label: "Documentation",
                            value:
                                "User manual / warranty information",
                        },
                    ],
                },
            ],
        },
        {
            id: "warranty",
            label: "Warranty",
            description:
                "Warranty, replacement and service information.",
            groups: [
                {
                    title: "Warranty Details",
                    rows: [
                        {
                            label: "Warranty Summary",
                            value:
                                "Manufacturer warranty",
                        },
                        {
                            label: "Replacement",
                            value:
                                "Replacement as per appliance policy",
                        },
                        {
                            label: "Service Support",
                            value:
                                "Manufacturer / authorised service support where applicable",
                        },
                        {
                            label: "Seller Support",
                            value:
                                "Salar seller assistance",
                        },
                    ],
                },
            ],
        },
        {
            id: "manufacturer",
            label: "Manufacturer Info",
            description:
                "Origin and manufacturer information for this appliance.",
            groups: [
                {
                    title: "Manufacturer Details",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Manufacturer",
                            value:
                                "As declared on the product label",
                        },
                        {
                            label: "Packer",
                            value:
                                "As declared on the product packaging",
                        },
                        {
                            label: "Country of Origin",
                            value:
                                "As per product label",
                        },
                        {
                            label: "Generic Name",
                            value:
                                "Home Appliance",
                        },
                        {
                            label: "Net Quantity",
                            value:
                                "1 unit",
                        },
                    ],
                },
            ],
        },
    ];

const buildGroceryItemDetails = (
    product: ProductDetailProduct
): ProductDetailTab[] => [
        {
            id: "specifications",
            label: "Specifications",
            description:
                "Pack, storage and product information.",
            groups: [
                {
                    title: "Product & Pack",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Product Name",
                            value:
                                product.title,
                        },
                        {
                            label: "Category",
                            value:
                                "Grocery & Everyday Needs",
                        },
                        {
                            label: "Pack Size",
                            value:
                                product.quantityOptions?.[0]
                                    ?.label ||
                                "Standard pack",
                        },
                        {
                            label: "Packaging",
                            value:
                                "Sealed retail pack",
                        },
                        {
                            label: "Shelf Life",
                            value:
                                "Refer to physical package",
                        },
                    ],
                },
                {
                    title: "Storage & Safety",
                    rows: [
                        {
                            label: "Storage",
                            value:
                                "Store in a cool, dry place",
                        },
                        {
                            label: "After Opening",
                            value:
                                "Keep sealed as recommended on pack",
                        },
                        {
                            label: "Allergens",
                            value:
                                "Refer to product packaging",
                        },
                        {
                            label: "Ingredients",
                            value:
                                "Refer to the physical manufacturer label",
                        },
                        {
                            label: "Nutrition",
                            value:
                                "Refer to the physical manufacturer label",
                        },
                    ],
                },
            ],
        },
        {
            id: "warranty",
            label: "Warranty",
            description:
                "Category support and replacement information.",
            groups: [
                {
                    title: "Support Details",
                    rows: [
                        {
                            label: "Warranty Applicability",
                            value:
                                "Not applicable for consumable grocery products unless specifically stated.",
                        },
                        {
                            label: "Return / Replacement",
                            value:
                                "Food-category policy applies based on the delivered condition and reported issue.",
                        },
                        {
                            label: "Seller Support",
                            value:
                                "Salar grocery support",
                        },
                    ],
                },
            ],
        },
        {
            id: "manufacturer",
            label: "Manufacturer Info",
            description:
                "Manufacturer-declared pack and origin details.",
            groups: [
                {
                    title: "Manufacturer Details",
                    rows: [
                        {
                            label: "Brand",
                            value:
                                product.brand ||
                                "SALAR",
                        },
                        {
                            label: "Manufacturer",
                            value:
                                "Refer to physical product packaging",
                        },
                        {
                            label: "Packer",
                            value:
                                "Refer to physical product packaging",
                        },
                        {
                            label: "Country of Origin",
                            value:
                                "As declared on pack",
                        },
                        {
                            label: "Generic Name",
                            value:
                                "Grocery Product",
                        },
                        {
                            label: "Net Quantity",
                            value:
                                product.quantityOptions?.[0]
                                    ?.label ||
                                "As per pack",
                        },
                    ],
                },
            ],
        },
    ];

const withItemDetails = (
    content: DetailContent,
    tabs: ProductDetailTab[]
): DetailContent => {
    const consolidatedTitles =
        new Set([
            "Item Details",
            "Technical Specifications",
            "Warranty & Support",
        ]);

    const cleanedSections =
        content.detailSections.filter(
            (
                section
            ) =>
                !consolidatedTitles.has(
                    section.title
                )
        );

    const aboutIndex =
        cleanedSections.findIndex(
            (
                section
            ) =>
                section.title ===
                "About this item"
        );

    const itemDetailsSection:
        DetailSection = {
        title:
            "Item Details",
        subtitle:
            "Specifications, warranty & manufacturer information",
        tabs,
    };

    if (
        aboutIndex <
        0
    ) {
        return {
            ...content,
            detailSections: [
                itemDetailsSection,
                ...cleanedSections,
            ],
        };
    }

    const nextSections =
        [
            ...cleanedSections,
        ];

    nextSections.splice(
        aboutIndex +
        1,
        0,
        itemDetailsSection
    );

    return {
        ...content,
        detailSections:
            nextSections,
    };
};

const appendFaqSection = (
    content: DetailContent,
    faqs: ProductDetailFaq[]
): DetailContent => ({
    ...content,
    detailSections: [
        ...content.detailSections,
        {
            title: "FAQs",
            subtitle:
                "Common questions about this product",
            faqs,
        },
    ],
});

const buildDynamicItemDetails = (
    product: ProductDetailProduct
): ProductDetailTab[] => {
    const tabs: ProductDetailTab[] = [];
    const dash = (value?: string) =>
        value && value !== "-" ? value : undefined;

    const specRows: ProductDetailTabRow[] =
        product.attributes && Object.keys(product.attributes).length > 0
            ? Object.entries(product.attributes).map(([label, value]) => ({
                label,
                value:
                    value !== undefined && value !== null && String(value).trim()
                        ? String(value)
                        : "-",
            }))
            : [];

    if (specRows.length > 0) {
        tabs.push({
            id: "specifications",
            label: "Specifications",
            description: "Complete product specifications as listed by the seller.",
            groups: [{ title: "Product Specifications", rows: specRows }],
        });
    }

    const manufacturerRows: ProductDetailTabRow[] = [
        { label: "Manufacturer", value: dash(product.vendors?.manufacturedBy) },
        { label: "Packer / Marketer", value: dash(product.vendors?.marketedBy) },
        { label: "Warranty", value: dash(product.warranty) },
        { label: "Return Policy", value: dash(product.returnPolicy) },
        { label: "Country of Origin", value: dash(product.country) },
        { label: "Packaging", value: dash(product.packaging) },
    ].filter((row): row is ProductDetailTabRow => Boolean(row.value)) as ProductDetailTabRow[];

    if (manufacturerRows.length > 0) {
        tabs.push({
            id: "manufacturer",
            label: "Manufacturer & Warranty",
            description: "Origin, manufacturer and warranty information.",
            groups: [{ title: "Manufacturer Details", rows: manufacturerRows }],
        });
    }

    const deliveryRows: ProductDetailTabRow[] = [
        product.freeDelivery && { label: "Free Delivery", value: "Yes" },
        product.fastDelivery && { label: "Fast Delivery", value: "Available" },
        product.nextDayDelivery && { label: "Next Day Delivery", value: "Available" },
        dash(product.shipsFrom) && { label: "Ships From", value: dash(product.shipsFrom) },
        dash(product.payment) && { label: "Payment", value: dash(product.payment) },
    ].filter(Boolean) as ProductDetailTabRow[];

    if (deliveryRows.length > 0) {
        tabs.push({
            id: "delivery",
            label: "Delivery & Payment",
            description: "Delivery speed, origin and payment options for this product.",
            groups: [{ title: "Delivery & Payment", rows: deliveryRows }],
        });
    }

    return tabs;
};

const buildDynamicFaqs = (
    product: ProductDetailProduct
): ProductDetailFaq[] => {
    if (product.faqs && product.faqs.length > 0) {
        return product.faqs;
    }

    const faqs: ProductDetailFaq[] = [];

    if (product.warranty && product.warranty !== "-") {
        faqs.push({
            question: "Does this product include a warranty?",
            answer: product.warranty,
        });
    }

    if (product.returnPolicy && product.returnPolicy !== "-") {
        faqs.push({
            question: "Can I return or replace this item?",
            answer: product.returnPolicy,
        });
    }

    faqs.push({
        question: "Which variant will I receive?",
        answer:
            "You will receive the colour, size or configuration selected on this product page before checkout. Recheck the selected option before placing the order.",
    });

    if (product.fastDelivery || product.nextDayDelivery || product.freeDelivery) {
        faqs.push({
            question: "Is fast or free delivery available?",
            answer: product.freeDelivery
                ? "Free delivery is available on this product. Delivery speed depends on your pincode and the option selected at checkout."
                : "Express delivery is available for this product on eligible pincodes.",
        });
    }

    return faqs;
};

export const buildDynamicDetailContent = (product: ProductDetailProduct): DetailContent => {
    const rawBrand =
        typeof product.brand === "string"
            ? product.brand
            : (product.brand as any)?.name;
    const brand =
        typeof rawBrand === "string" && rawBrand.trim() && rawBrand !== "-"
            ? rawBrand.trim()
            : "-";
    const category = product.category && product.category !== "-" ? product.category : "-";

    const attributeRows = product.attributes && Object.keys(product.attributes).length > 0
        ? Object.entries(product.attributes).map(([label, value]) => ({
            label,
            value: value !== undefined && value !== null && String(value).trim() ? String(value) : "-",
        }))
        : [];

    const topHighlightsRows = attributeRows.length > 0
        ? attributeRows
        : [
            { label: "Brand", value: brand },
            { label: "Category", value: category },
        ];

    const aboutBullets = product.bulletPoints && product.bulletPoints.length > 0
        ? product.bulletPoints
        : (product.description && product.description !== "-" ? [product.description] : ["-"]);

    const colors = product.colors && product.colors.length > 0
        ? product.colors.map((c) => ({ ...c, image: c.image || product.image }))
        : undefined;

    const sizes = product.variants && product.variants.length > 0
        ? product.variants.map((v) => v.label)
        : (product.sizes && product.sizes.length > 0 ? product.sizes : undefined);

    // Dynamic Shopping Policies
    const shippingTitle =
        product.fastDelivery || product.nextDayDelivery ? "Fast Shipping" : "Shipping";
    const shippingDesc =
        product.shipsFrom && product.shipsFrom !== "-"
            ? `Ships from ${product.shipsFrom}`
            : product.fastDelivery || product.nextDayDelivery
            ? "Priority dispatch within 24h"
            : product.freeDelivery
            ? "Standard dispatch within 1-2 business days"
            : "Standard dispatch available";

    const deliveryTitle =
        product.freeDelivery ? "Free Delivery" : "Delivery";
    const deliveryDesc =
        product.freeDelivery
            ? "Free doorstep delivery on this order"
            : product.fastDelivery || product.nextDayDelivery
            ? "Express delivery available to your pincode"
            : "Doorstep delivery with real-time tracking";

    const catStr = (
        (product.category || "") +
        " " +
        (product.title || "")
    ).toLowerCase();

    let returnsTitle = "Returns & Exchange";
    let returnsDesc = product.returnPolicy || product.warranty || "7-day return & exchange";

    if (product.warranty) {
        returnsTitle = "Brand Warranty";
        returnsDesc = product.warranty;
    } else if (product.returnPolicy) {
        returnsTitle = "Returns & Replacement";
        returnsDesc = product.returnPolicy;
    } else if (
        catStr.includes("electronic") ||
        catStr.includes("mobile") ||
        catStr.includes("phone") ||
        catStr.includes("laptop") ||
        catStr.includes("tablet")
    ) {
        returnsTitle = "7 Days Replacement";
        returnsDesc = "7-day replacement for hardware defects & brand warranty applies";
    } else if (
        catStr.includes("fashion") ||
        catStr.includes("men") ||
        catStr.includes("women") ||
        catStr.includes("cloth") ||
        catStr.includes("shoe")
    ) {
        returnsTitle = "7 Days Return";
        returnsDesc = "Hassle-free 7-day return and exchange available for size and fit";
    } else if (
        catStr.includes("grocer") ||
        catStr.includes("food") ||
        catStr.includes("fresh")
    ) {
        returnsTitle = "Quality Guarantee";
        returnsDesc = "Non-returnable. Replacement or refund for damaged or expired items";
    } else if (
        catStr.includes("home") ||
        catStr.includes("appliance") ||
        catStr.includes("furniture")
    ) {
        returnsTitle = "10 Days Replacement";
        returnsDesc = "Free replacement in case of transit damage or manufacturing defect";
    }

    const paymentTitle =
        product.payment && product.payment !== "-" && product.payment !== "Secure transaction"
            ? product.payment
            : "Pay on Delivery";
    const paymentDesc =
        product.payment && product.payment !== "-" && product.payment !== "Secure transaction"
            ? `Protected checkout with ${product.payment.toLowerCase()} options`
            : "Available on eligible pincodes & online payment";

    const securityTitle = "Secure Transaction";
    const securityDesc = "256-bit SSL encrypted & protected checkout";

    const content: DetailContent = {
        storeName: brand,
        pickLabel: product.badge || "-",
        pickText: product.badge ? "Popular for fit, quality and overall value" : "",
        colorLabel: colors?.length ? "Colours" : undefined,
        sizeLabel: sizes?.length ? "Select Size / Option" : undefined,
        showSizeGuide: false,
        colors,
        sizes,
        offers,
        policies: {
            shippingTitle,
            shipping: shippingDesc,
            deliveryTitle,
            delivery: deliveryDesc,
            returnsTitle,
            returns: returnsDesc,
            paymentTitle,
            payment: paymentDesc,
            securityTitle,
            security: securityDesc,
        },
        reviewCount: product.reviews ? `${product.reviews} ratings` : "-",
        detailSections: [
            {
                title: "Top Highlights",
                subtitle: "Core product specifications",
                rows: topHighlightsRows,
            },
            {
                title: "About this item",
                subtitle: "Product details & highlights",
                bullets: aboutBullets,
            },
            {
                title: "Additional Information",
                subtitle: "Origin and product details",
                rows: [
                    { label: "Brand", value: brand },
                    { label: "Category", value: category },
                    { label: "Country of Origin", value: product.country || "-" },
                    ...(product.vendors?.manufacturedBy ? [{ label: "Manufacturer", value: product.vendors.manufacturedBy }] : []),
                    ...(product.vendors?.marketedBy ? [{ label: "Packer / Marketer", value: product.vendors.marketedBy }] : []),
                    { label: "Seller Support", value: "-" },
                ],
            },
            {
                title: "Item Description",
                subtitle: "More information",
                paragraph: product.description && product.description !== "-" ? product.description : "-",
            },
        ],
    };

    const tabs = buildDynamicItemDetails(product);
    const faqs = buildDynamicFaqs(product);
    const withTabs = tabs.length ? withItemDetails(content, tabs) : content;
    return faqs.length ? appendFaqSection(withTabs, faqs) : withTabs;
};

export const buildProductDetailContent = (
    product: ProductDetailProduct
): DetailContent => {
    // If dynamic catalog product (has attributes, bulletPoints, or string id)
    if (
        product.attributes ||
        (product.bulletPoints && product.bulletPoints.length > 0) ||
        typeof product.id === "string"
    ) {
        return buildDynamicDetailContent(product);
    }

    const category = (
        product.category || ""
    ).toLowerCase();

    if (category.includes("women")) {
        return appendFaqSection(
            withItemDetails(
                fashionDetails(product, true),
                buildFashionItemDetails(product, true)
            ),
            buildFashionFaqs(product, true)
        );
    }

    if (
        category.includes("men") ||
        category.includes("fashion")
    ) {
        return appendFaqSection(
            withItemDetails(
                fashionDetails(product, false),
                buildFashionItemDetails(product, false)
            ),
            buildFashionFaqs(product, false)
        );
    }

    if (
        category.includes("electronic")
    ) {
        return appendFaqSection(
            withItemDetails(
                electronicsDetails(product),
                buildElectronicsItemDetails(product)
            ),
            buildElectronicsFaqs(product)
        );
    }

    if (
        category.includes("appliance") ||
        category.includes("home")
    ) {
        return appendFaqSection(
            withItemDetails(
                applianceDetails(product),
                buildApplianceItemDetails(product)
            ),
            buildApplianceFaqs(product)
        );
    }

    if (
        category.includes("grocer") ||
        category.includes("fresh")
    ) {
        return appendFaqSection(
            withItemDetails(
                groceryDetails(product),
                buildGroceryItemDetails(product)
            ),
            buildGroceryFaqs(product)
        );
    }

    return appendFaqSection(
        withItemDetails(
            fashionDetails(product, false),
            buildFashionItemDetails(product, false)
        ),
        buildFashionFaqs(product, false)
    );
};
